// Libs
import { useSelector } from 'react-redux';
// Utils
import GENERAL from 'utils/constants/general';
import { unifyDatetime } from 'utils/libs/dateFormats';
// Services
import ApiServiceV2 from 'services/apiService/apiServiceV2';
// Selectors
import {
	selectSelectedProjectId,
	selectSelectedOrderTargetFilterId,
} from 'core/selectors';
import { selectProfile } from 'screens/Login/selectors';

const { SUCCESS } = GENERAL.ENV.STATUS;

const oldEndpoint = '/orders';
const endpoint = '/orderManager';
const useOrderApi = () => {
	const selectedProjectId = useSelector(selectSelectedProjectId);
	const selectedOrderTargetFilterId = useSelector(
		selectSelectedOrderTargetFilterId,
	);
	const profile = useSelector(selectProfile);

	return {
		/**
		 * Get assignment data on create process
		 * @param {Object[]} orders
		 * @param {string} dateFormat
		 * @param {int} validationTemplateId
		 * @returns orders with user assignment
		 */
		getCreationAssignment: (
			orders,
			{
				dateFormat,
				validationTemplateId,
				assignmentReviewSnapshot,
				fieldReviewSnapshot,
				zoneCreationSelections,
				serviceCreationSelections,
				serviceReviewSelections,
				assignmentReviewSelections,
				fieldReviewSelections,
				contractFrequencyReviewSelections,
				contractFrequencyCreationRequests,
			},
		) =>
			ApiServiceV2.post({
				url: `${endpoint}/v1/${selectedProjectId}/${selectedOrderTargetFilterId}/creationAssignment`,
				data: {
					orders,
					dateFormat,
					validationTemplateId,
					...(Array.isArray(assignmentReviewSnapshot)
						? { assignmentReviewSnapshot }
						: {}),
					...(Array.isArray(fieldReviewSnapshot)
						? { fieldReviewSnapshot }
						: {}),
					...(Array.isArray(zoneCreationSelections)
						? { zoneCreationSelections }
						: {}),
					...(Array.isArray(serviceCreationSelections)
						? { serviceCreationSelections }
						: {}),
					...(Array.isArray(serviceReviewSelections)
						? { serviceReviewSelections }
						: {}),
					...(Array.isArray(assignmentReviewSelections)
						? { assignmentReviewSelections }
						: {}),
					...(Array.isArray(fieldReviewSelections)
						? { fieldReviewSelections }
						: {}),
					...(Array.isArray(contractFrequencyReviewSelections)
						? { contractFrequencyReviewSelections }
						: {}),
					...(Array.isArray(contractFrequencyCreationRequests)
						? { contractFrequencyCreationRequests }
						: {}),
				},
				// Review recovery needs reason/domain/orders from the structured 409.
				// Flattening it to errors[] would lose the stale-session context.
				preserveErrorEnvelope: true,
			}),
		/**
		 * Create imported orders
		 * @param {Object[]} orders
		 * @param {string} dateFormat
		 * @param {int} validationTemplateId
		 * @param {boolean} approveZoneCoverageChanges explicit final approval
		 * @param {Object[]} assignmentReviewSnapshot exact reviewed assignment per row
		 * @param {boolean} approveAssignmentChanges explicit approval after assignment drift
		 * @returns orders with user assignment
		 */
		create: ({
			orders,
			dateFormat,
			validationTemplateId,
			approveZoneCoverageChanges,
			assignmentReviewSnapshot,
			approveAssignmentChanges,
			zoneCreationSelections,
			approveZoneCreationChanges,
			serviceCreationSelections,
			approveServiceCreationChanges,
			serviceReviewSelections,
			fieldReviewSnapshot,
			contractFrequencyReviewSelections,
			contractFrequencyCreationSelections,
			approveContractFrequencyCreationChanges,
		}) =>
			ApiServiceV2.post({
				url: `${endpoint}/v1/${selectedProjectId}/${selectedOrderTargetFilterId}/create`,
				data: {
					orders,
					dateFormat,
					validationTemplateId,
					...(approveZoneCoverageChanges === true
						? { approveZoneCoverageChanges: true }
						: {}),
					...(Array.isArray(assignmentReviewSnapshot)
						? { assignmentReviewSnapshot }
						: {}),
					...(approveAssignmentChanges === true
						? { approveAssignmentChanges: true }
						: {}),
					...(Array.isArray(zoneCreationSelections)
						? { zoneCreationSelections }
						: {}),
					...(approveZoneCreationChanges === true
						? { approveZoneCreationChanges: true }
						: {}),
					...(Array.isArray(serviceCreationSelections)
						? { serviceCreationSelections }
						: {}),
					...(approveServiceCreationChanges === true
						? { approveServiceCreationChanges: true }
						: {}),
					...(Array.isArray(serviceReviewSelections)
						? { serviceReviewSelections }
						: {}),
					...(Array.isArray(fieldReviewSnapshot)
						? { fieldReviewSnapshot }
						: {}),
					...(Array.isArray(contractFrequencyReviewSelections)
						? { contractFrequencyReviewSelections }
						: {}),
					...(Array.isArray(contractFrequencyCreationSelections)
						? { contractFrequencyCreationSelections }
						: {}),
					...(approveContractFrequencyCreationChanges === true
						? { approveContractFrequencyCreationChanges: true }
						: {}),
				},
				// Import preview needs the rejected orders to render row-level errors.
				// Other API consumers keep the historical errors[] rejection shape.
				preserveErrorEnvelope: true,
			}),

		/**
		 * Get user assignment
		 * @param {Object[]} orders
		 * @param {int} serviceId
		 * @param {string} searchValue
		 * @returns orders[...].userAssignment
		 */
		getUserAssignment: (orders, { serviceId, searchValue }) => {
			const _orders = Buffer.from(JSON.stringify(orders)).toString('base64');
			const _searchValue = searchValue
				? Buffer.from(searchValue).toString('base64')
				: undefined;

			return ApiServiceV2.get({
				url: `${endpoint}/v1/${selectedProjectId}/${selectedOrderTargetFilterId}/${serviceId}/${_orders}/${_searchValue}/userAssignment`,
			});
		},
		/**
		 * Set user assignment
		 * @param {Object[]} orders
		 * @param {int} serviceId
		 * @param {Object} coords
		 * @returns {Object[]} orders
		 */
		setUserAssignment: (orders, { serviceId, coords }) => {
			return ApiServiceV2.put({
				url: `${endpoint}/v1/${selectedProjectId}/${selectedOrderTargetFilterId}/${serviceId}/userAssignment`,
				data: { orders, coords },
			});
		},

		/**
		 * Set service assignment
		 * @param {int} orderId
		 * @param {int} serviceId
		 * @returns {object} The updated order data
		 */
		setServiceAssignment: (orderId, serviceId) => {
			return ApiServiceV2.put({
				url: `${endpoint}/v1/serviceAssignment`,
				data: { orderId, serviceId },
			});
		},

		/**
		 * Set event assignment
		 * @param {Object[]} orders
		 * @returns {Object[]} orders
		 */
		setEventAssignment: (orders, { coords }) => {
			return ApiServiceV2.put({
				url: `${endpoint}/v1/${selectedProjectId}/eventAssignment`,
				data: { orders, coords },
			});
		},

		/**
		 * Set event assignment
		 * @param {Object[]} orders
		 * @returns {Object[]} orders
		 */
		setEventAssignmentV0: (
			orders,
			{ eventId, coords, comment, reprogramDate, reprogramTime },
		) => {
			return ApiServiceV2.put({
				url: `${oldEndpoint}/event_admin`,
				data: {
					selectedOrders: orders.map(order => ({ order_id: order.id })),
					eventId,
					comment,
					reprogramAt: unifyDatetime(
						reprogramDate,
						reprogramTime,
						profile.organization.tz,
					),
					coords,
				},
			}).then(eventData =>
				orders.map(order => ({
					...order,
					status: SUCCESS,
				})),
			);
		},

		/**
		 * Set zone assignment
		 * @param {int} zoneId
		 * @param {int} orderId
		 * @returns {Object}
		 */
		setZoneAssignment: (zoneId, orderId) => {
			return ApiServiceV2.put({
				url: `${endpoint}/v1/${zoneId}/${orderId}/zoneAssignment`,
				data: { zoneId, orderId },
			});
		},

		/**
		 * Get zone assignment
		 * @param {Object[]} orders
		 * @param {int} serviceId
		 * @param {string} searchValue
		 * @returns orders[...].zoneAssignment
		 */
		getZoneAssignment: ({ searchValue }) => {
			const _searchValue = !!searchValue
				? Buffer.from(searchValue).toString('base64')
				: undefined;

			return ApiServiceV2.get({ url: `zones/nestedTotalZones` });
		},

		/**
		 * Get zone assignment
		 * @param {int} orderId
		 * @returns Object[]
		 */
		getOrderHistory: orderId =>
			ApiServiceV2.get({
				url: `${oldEndpoint}/history/${orderId}`,
			}),
		/**
		 * Set Details
		 * @param {int} orderId
		 * @returns Object[]
		 */
		setOrderDetails: ({ orderId, rowData }) =>
			ApiServiceV2.post({
				url: `${oldEndpoint}/details/${orderId}`,
				data: { rowData },
			}),

		/**
		 * Get zone assignment
		 * @param {int} orderId
		 * @returns Object[]
		 */
		getBillingMaterials: orderId =>
			ApiServiceV2.get({
				url: `/warehouses_transactions_docs/getBillingMaterials/${orderId}`,
			}),
	};
};

export default useOrderApi;
